import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { useEffect, useState } from "react"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material"
import { confirm } from "lib/@dialogs/confirm"
import { ResetExcelImport } from "event-definitions"
import { importData } from "./controller/import-data"
import { navigate } from "lib/routes/navigate"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"

export function Finished({ active = false }) {
    const [importing, setImporting] = useState(false)
    const [sheet] = useBoundValue("importFromSheet")
    const [row] = useBoundValue("headerRow")
    const [mapping] = useBoundValue("fields", {})
    const [file] = useBoundValue("fileContents")
    const [filename] = useBoundValue("fileName")
    const { register } = useBoundContext()
    const [importResult, setImportResult] = useState(null)
    const rowsImported = importResult?.totalImported ?? 0
    const invalidQuantity = importResult?.invalidQuantity
    const invalidQuantityMessage = formatInvalidQuantityWarning(invalidQuantity)

    useEffect(() => {
        load().catch(console.error)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sheet, row, mapping, file])
    return (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 3 }}>
            {!!importing && (
                <Box>
                    <CircularProgress size={32} />
                </Box>
            )}
            {!!importing && <Typography>Importing &quot;{filename}&quot;</Typography>}
            {!importing && (
                <Stack spacing={3}>
                    <Typography>
                        Imported {rowsImported.toLocaleString()} {"row".pluralize(rowsImported)} from &quot;{filename}
                        &quot;
                    </Typography>
                    {!!invalidQuantityMessage && (
                        <Alert severity="warning" data-cy="invalid-quantity-warning">
                            {invalidQuantityMessage}
                        </Alert>
                    )}
                    <Stack direction="row" spacing={1}>
                        <Button
                            data-cy="reset-excel-import-btn"
                            variant="outlined"
                            onClick={reset}
                            sx={{ width: "max-content" }}
                        >
                            Reset
                        </Button>
                        <Button
                            data-cy="finish-excel-import-btn"
                            variant="contained"
                            onClick={finish}
                            sx={{ width: "max-content" }}
                        >
                            Finish
                        </Button>
                    </Stack>
                </Stack>
            )}
        </Stack>
    )

    async function load() {
        if (!file) {
            if (active) errorSnackbar("No file content available. Please go back and re-select the file.")
            return
        }
        if (!sheet?.name) {
            if (active) errorSnackbar("No worksheet selected. Please go back and select a worksheet.")
            return
        }
        setImporting(true)
        try {
            const result = await importData({
                file,
                mapping,
                row,
                sheet: sheet.name,
                registerId: register.id,
                filename,
            })
            setImportResult(result)
        } catch (e) {
            errorSnackbar(`Failed to import excel file: ${e.message}`)
            console.error(e.message)
            setImporting(false)
            return
        }
        setImporting(false)
        successSnackbar("File successfully imported")
    }

    async function reset() {
        if (
            await confirm(
                "This will clear your current selections. Previously imported data won't be affected. Start over?",
                "Reset"
            )
        ) {
            ResetExcelImport.raise()
        }
    }

    function finish() {
        navigate(`/app/assets/${register.id}?asset-tabsTab=asset%20list`)
    }
}

function formatInvalidQuantityWarning(invalidQuantity) {
    if (!invalidQuantity || invalidQuantity.count < 1) return ""
    const { count, samples = [] } = invalidQuantity
    const sampleText = samples.map(formatSample).join(", ")
    const remainder = count > samples.length ? ` and ${count - samples.length} more` : ""
    return `Some Quantity values could not be imported because they were not valid numbers. Examples: ${sampleText}${remainder}. Those assets have been imported without a quantity value (defaulting to 1 for display).`

    function formatSample({ row, tag, value }) {
        const parts = [`row ${row}`]
        if (tag) {
            parts.push(`tag ${tag}`)
        }
        if (value) {
            parts.push(`value "${value}"`)
        }
        return parts.join(" – ")
    }
}
